import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DatePicker from "@components/ui/selection/DatePicker";
import { useState, useMemo } from "react";
import { useVehiculesStore } from "@store/vehiculesStore";
import { navigateTo } from "context/navigation.service";
import type {
  Vehicle,
  VehicleTag,
  VehicleStatus,
  VehicleFuelType,
  VehicleColoris,
  VehicleReservation,
} from "types/garage/vehicules";

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black",
  blanc: "white",
  gris: "grey",
  bleu: "blue",
  rouge: "red",
  vert: "green",
  jaune: "yellow",
  marron: "brown",
  orange: "orange",
  autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const modelFamily = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${modelFamily}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" },
  { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" },
  { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" },
  { value: "vert", label: "Vert" },
  { value: "jaune", label: "Jaune" },
  { value: "marron", label: "Marron" },
  { value: "orange", label: "Orange" },
  { value: "autre", label: "Autre" },
];

const MARQUE_OPTIONS = [
  { value: "CITROEN", label: "CITROËN" },
  { value: "PEUGEOT", label: "PEUGEOT" },
  { value: "OPEL", label: "OPEL" },
];

const MODELES_BY_MARQUE: Record<string, { value: string; label: string }[]> = {
  CITROEN: [
    { value: "C1", label: "C1" },
    { value: "C3", label: "C3" },
    { value: "C3 AIRCROSS", label: "C3 AIRCROSS" },
    { value: "C4", label: "C4" },
    { value: "C4 PICASSO", label: "C4 PICASSO" },
    { value: "C4 CACTUS", label: "C4 CACTUS" },
    { value: "C5", label: "C5" },
    { value: "C5 AIRCROSS", label: "C5 AIRCROSS" },
    { value: "BERLINGO", label: "BERLINGO" },
    { value: "BERLINGO VP", label: "BERLINGO VP" },
    { value: "BERLINGO VAN", label: "BERLINGO VAN" },
    { value: "JUMPY", label: "JUMPY" },
    { value: "JUMPER", label: "JUMPER" },
    { value: "SPACETOURER", label: "SPACETOURER" },
    { value: "E-C4", label: "Ë-C4" },
    { value: "E-BERLINGO", label: "Ë-BERLINGO" },
    { value: "AMI", label: "AMI" },
  ],
  PEUGEOT: [
    { value: "108", label: "108" },
    { value: "208", label: "208" },
    { value: "E-208", label: "E-208" },
    { value: "301", label: "301" },
    { value: "308", label: "308" },
    { value: "308 SW", label: "308 SW" },
    { value: "408", label: "408" },
    { value: "508", label: "508" },
    { value: "508 SW", label: "508 SW" },
    { value: "2008", label: "2008" },
    { value: "E-2008", label: "E-2008" },
    { value: "3008", label: "3008" },
    { value: "5008", label: "5008" },
    { value: "PARTNER", label: "PARTNER" },
    { value: "RIFTER", label: "RIFTER" },
    { value: "EXPERT", label: "EXPERT" },
    { value: "BOXER", label: "BOXER" },
    { value: "TRAVELLER", label: "TRAVELLER" },
    { value: "BIPPER", label: "BIPPER" },
  ],
  OPEL: [
    { value: "CORSA", label: "CORSA" },
    { value: "CORSA-E", label: "CORSA-E" },
    { value: "ASTRA", label: "ASTRA" },
    { value: "ASTRA SPORTS TOURER", label: "ASTRA SPORTS TOURER" },
    { value: "CROSSLAND", label: "CROSSLAND" },
    { value: "GRANDLAND", label: "GRANDLAND" },
    { value: "MOKKA", label: "MOKKA" },
    { value: "MOKKA-E", label: "MOKKA-E" },
    { value: "COMBO", label: "COMBO" },
    { value: "COMBO LIFE", label: "COMBO LIFE" },
    { value: "VIVARO", label: "VIVARO" },
    { value: "MOVANO", label: "MOVANO" },
    { value: "ZAFIRA", label: "ZAFIRA" },
    { value: "ZAFIRA LIFE", label: "ZAFIRA LIFE" },
    { value: "ROCKS-E", label: "ROCKS-E" },
  ],
};

const TYPE_OPTIONS = [
  { value: "pret", label: "Véhicule de prêt" },
  { value: "location", label: "Véhicule de location" },
  { value: "velos", label: "Vélo" },
];

const FUEL_OPTIONS = [
  { value: "diesel", label: "Diesel" },
  { value: "essence", label: "Essence" },
  { value: "electrique", label: "Electrique" },
  { value: "gpl", label: "GPL" },
  { value: "hybride", label: "Hybride" },
  { value: "autre", label: "Autre" },
];

const CATEGORY_OPTIONS = [
  { value: "autre", label: "Autre" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "mecanique", label: "Mécanique" },
];

const STATUS_OPTIONS = [
  { value: "disponible", label: "Disponible" },
  { value: "en_cours", label: "En cours d'utilisation" },
  { value: "hors_service", label: "Hors service" },
  { value: "reserve", label: "Réservé" },
];

const TAG_OPTIONS: { value: VehicleTag; label: string }[] = [
  { value: "huile", label: "HUILE" },
  { value: "rendu_sale", label: "Rendu sale" },
  { value: "reserve", label: "Réservé" },
  { value: "assurance", label: "Assurance" },
  { value: "a_sortir", label: "À sortir du parc" },
  { value: "plein_fait", label: "Plein fait" },
  { value: "hors_service", label: "Hors service" },
];

const EMPTY_VEHICLE: Vehicle = {
  id: "",
  name: "",
  plate: "",
  km: 0,
  category: "autre",
  usage: "pret",
  fuel_type: "diesel",
  designation: "",
  tags: [],
  modele: "",
  status: "disponible",
  has_print: false,
};

function formatDateFr(dateStr: string): string {
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
}

function formatHour(h: number): string {
  const hours = Math.floor(h);
  const minutes = Math.round((h - hours) * 60);
  return `${String(hours).padStart(2, "0")}h${minutes > 0 ? String(minutes).padStart(2, "0") : "00"}`;
}

function formatReservationTime(r: VehicleReservation): string {
  const isSingleDay = r.startDate === r.endDate;
  const start = formatDateFr(r.startDate);
  const end = formatDateFr(r.endDate);

  if (isSingleDay && r.startHour != null && r.durationHours != null) {
    return `${start} · ${formatHour(r.startHour)} – ${formatHour(r.startHour + r.durationHours)}`;
  }

  const startTime = r.startHour != null ? ` à ${formatHour(r.startHour)}` : "";
  const endTime = r.returnHour != null ? ` à ${formatHour(r.returnHour)}` : "";

  return `${start}${startTime} → ${end}${endTime}`;
}

function VehicleReservationsSection({ vehicleId }: { vehicleId: string }) {
  const reservations = useVehiculesStore((s) => s.reservations);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const vehicleRes = useMemo(
    () => reservations.filter((r) => r.vehicleId === vehicleId),
    [reservations, vehicleId],
  );

  const currentRes = useMemo(
    () => vehicleRes.find((r) => r.startDate <= today && r.endDate >= today),
    [vehicleRes, today],
  );

  const upcomingRes = useMemo(
    () =>
      vehicleRes
        .filter((r) => r.startDate > today)
        .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [vehicleRes, today],
  );

  const handleGoToReservation = (r: VehicleReservation) => {
    navigateTo({ route: `/vehicules?reservation=${r.id}` });
  };

  if (!currentRes && upcomingRes.length === 0) {
    return (
      <div className="px-4 pb-2">
        <div className="flex flex-row items-center gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <Icon1 name="calendar" size={16} color="#A1A1A1" />
          <span className="text-h3 text-neutral-400">Aucune réservation à venir</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-2 flex flex-col gap-2">
      <span className="text-h3 font-medium text-neutral-500">Réservations</span>

      {currentRes && (
        <div
          className="flex flex-row items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => handleGoToReservation(currentRes)}
        >
          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-h3 font-medium text-blue-700">En prêt — {currentRes.clientName}</span>
            <span className="text-h4 text-blue-500">
              {formatReservationTime(currentRes)}
            </span>
          </div>
          <Icon1 name="chevron-right" size={16} color="#3B82F6" />
        </div>
      )}

      {upcomingRes.slice(0, 3).map((r) => (
        <div
          key={r.id}
          className="flex flex-row items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200 cursor-pointer hover:bg-neutral-100 transition-colors"
          onClick={() => handleGoToReservation(r)}
        >
          <div className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-h3 font-medium text-neutral-700">{r.clientName}</span>
            <span className="text-h4 text-neutral-400">
              {formatReservationTime(r)}
            </span>
          </div>
          <Icon1 name="chevron-right" size={16} color="#A1A1A1" />
        </div>
      ))}

      {upcomingRes.length > 3 && (
        <span className="text-h4 text-neutral-400 text-center">
          + {upcomingRes.length - 3} autre{upcomingRes.length - 3 > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

interface VehiculeModalProps {
  vehicle?: Vehicle;
  onClose: () => void;
  onSave: (vehicle: Vehicle) => void;
}

export default function VehiculeModal({ vehicle, onClose, onSave }: VehiculeModalProps) {
  const isCreate = !vehicle;
  const [form, setForm] = useState<Vehicle>({ ...(vehicle || EMPTY_VEHICLE) });
  const [imageError, setImageError] = useState(false);

  const imageUrl = useMemo(
    () => getVehicleImageUrl(form.marque, form.modele, form.coloris),
    [form.marque, form.modele, form.coloris],
  );

  const modeleOptions = useMemo(
    () => MODELES_BY_MARQUE[form.marque || ""] || [],
    [form.marque],
  );

  const update = <K extends keyof Vehicle>(key: K, value: Vehicle[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === "marque" || key === "modele" || key === "coloris") {
      setImageError(false);
    }
  };

  const handleSave = () => {
    onSave(form);
    onClose();
  };

  return (
    <BaseModal
      title={isCreate ? "Nouveau véhicule" : "Modifier un Véhicule"}
      startIcon={<Icon1 name={isCreate ? "plus" : "modify"} size={20} />}
      onClose={onClose}
      width="max-w-2xl"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Image area */}
        <div
          className="w-full h-200 rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden"
        >
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={form.name}
              className="w-full h-full object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-neutral-400">
              <Icon1 name="image" size={40} color="#D4D4D4" />
              <span className="text-h3 font-medium">
                {form.marque && form.modele ? "Image non disponible" : "Sélectionnez une marque et un modèle"}
              </span>
            </div>
          )}
        </div>

        {/* Nom / Coloris */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Nom"
              value={form.name}
              onChange={(v) => update("name", v)}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Coloris"
              options={COLORIS_OPTIONS}
              value={form.coloris || ""}
              onSelect={(v) => update("coloris", v as VehicleColoris)}
            />
          </div>
        </div>

        {/* Marque / MEC */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDown
              label="Marque"
              options={MARQUE_OPTIONS}
              value={form.marque || ""}
              onSelect={(v) => {
                update("marque", v as string);
                if (v !== form.marque) update("modele", "");
              }}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              label="MEC"
              seconds={form.mec || null}
              onChange={(s) => update("mec", s)}
            />
          </div>
        </div>

        {/* Modèle / VIN */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDown
              label="Modèle"
              options={modeleOptions}
              value={form.modele || ""}
              onSelect={(v) => update("modele", v as string)}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="VIN"
              value={form.vin || ""}
              onChange={(v) => update("vin", v)}
            />
          </div>
        </div>

        {/* Immatriculation / Km */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Immatriculation"
              value={form.plate}
              onChange={(v) => update("plate", v)}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="Km"
              type="number"
              value={String(form.km)}
              onChange={(v) => update("km", Number(v) || 0)}
            />
          </div>
        </div>

        {/* Type / Status */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDown
              label="Type"
              options={TYPE_OPTIONS}
              value={form.usage}
              onSelect={(v) => update("usage", v as Vehicle["usage"])}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Status"
              options={STATUS_OPTIONS}
              value={form.status}
              onSelect={(v) => update("status", v as VehicleStatus)}
            />
          </div>
        </div>

        {/* Fuel type / Category */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDown
              label="Carburant"
              options={FUEL_OPTIONS}
              value={form.fuel_type}
              onSelect={(v) => update("fuel_type", v as VehicleFuelType)}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Catégorie"
              options={CATEGORY_OPTIONS}
              value={form.category}
              onSelect={(v) => update("category", v as Vehicle["category"])}
            />
          </div>
        </div>

        {/* Notes */}
        <AreaTextField
          label="Notes"
          value={form.notes || ""}
          onChange={(v) => update("notes", v)}
          rows={3}
        />

        {/* Tags */}
        <DropDownMultiple
          label="Tags"
          options={TAG_OPTIONS}
          value={form.tags}
          onSelect={(items) => update("tags", items as VehicleTag[])}
        />
      </div>

      {/* Reservations */}
      {!isCreate && <VehicleReservationsSection vehicleId={form.id} />}

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Fermer
        </SecondaryButton>
        <Button
          variant="small"
          label={isCreate ? "Créer" : "Enregistrer"}
          onClick={handleSave}
        />
      </div>
    </BaseModal>
  );
}
